import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cs_admin_token";

const TYPES = [
  { value: "principal", label: "Principal" },
  { value: "family-office", label: "Family office" },
  { value: "hospitality", label: "Hospitality" },
  { value: "other", label: "Other" },
  { value: "", label: "Generic / Untagged" },
];
const LOCALES = [
  { value: "en", label: "EN" },
  { value: "pt", label: "PT" },
  { value: "ar", label: "AR" },
];

export default function EmailPreview() {
  const token = localStorage.getItem(TOKEN_KEY);
  const [params, setParams] = useSearchParams();
  const [preview, setPreview] = useState({ subject: "", html: "" });
  const [loading, setLoading] = useState(false);

  const leadType = params.has("type") ? params.get("type") : "principal";
  const locale = params.get("locale") || "en";
  const name = params.get("name") || "Sample Client";

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/admin/email-preview`, {
        params: { lead_type: leadType || undefined, locale, name },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setPreview(r.data))
      .finally(() => setLoading(false));
  }, [leadType, locale, name, token]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#070E13] text-[#F4EEE8] flex items-center justify-center">
        <Link to="/admin" className="text-[#C7A26C] underline">Sign in to admin first</Link>
      </div>
    );
  }

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-[#EDE3D0] py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8 border-b border-[#070E13]/15 pb-6">
          <div>
            <Link to="/admin" data-testid="email-preview-back" className="text-xs uppercase tracking-[0.2em] text-[#070E13]/60 hover:text-[#070E13] flex items-center gap-2">
              <ArrowLeft size={14} /> Inquiries
            </Link>
            <h1 className="font-serif text-4xl mt-3 text-[#070E13]">Auto-reply templates</h1>
            <p className="text-sm text-[#070E13]/60 mt-2">Preview each lead-type × locale combination. Resend is fired automatically when a contact is created (skipped if RESEND_API_KEY is empty).</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-6 mb-8">
          <div>
            <p className="overline text-[#070E13]/60 mb-2">Lead type</p>
            <div className="flex flex-wrap gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.value || "untagged"}
                  data-testid={`preview-type-${t.value || "generic"}`}
                  onClick={() => set("type", t.value)}
                  className={`px-3 py-2 text-xs uppercase tracking-[0.18em] border ${
                    leadType === t.value ? "bg-[#070E13] text-[#F4EEE8] border-[#070E13]" : "border-[#070E13]/25 hover:border-[#070E13]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="overline text-[#070E13]/60 mb-2">Locale</p>
            <div className="flex gap-2">
              {LOCALES.map((l) => (
                <button
                  key={l.value}
                  data-testid={`preview-locale-${l.value}`}
                  onClick={() => set("locale", l.value)}
                  className={`px-3 py-2 text-xs uppercase tracking-[0.18em] border ${
                    locale === l.value ? "bg-[#070E13] text-[#F4EEE8] border-[#070E13]" : "border-[#070E13]/25 hover:border-[#070E13]"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="overline text-[#070E13]/60 mb-2">Sample name</p>
            <input
              data-testid="preview-name"
              value={name}
              onChange={(e) => set("name", e.target.value)}
              className="bg-transparent border-b border-[#070E13]/30 focus:border-[#C7A26C] outline-none py-2 px-2 text-[#070E13] w-64"
            />
          </div>
        </div>

        <p className="text-sm font-serif text-[#070E13] mb-3"><strong>Subject:</strong> {loading ? "…" : preview.subject}</p>
        <iframe
          data-testid="email-preview-iframe"
          title="Email preview"
          srcDoc={preview.html}
          className="w-full bg-[#EDE3D0] border border-[#070E13]/15"
          style={{ height: "75vh" }}
        />
      </div>
    </div>
  );
}
